import React, { useState } from 'react'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { Crown, Link2, Lock, PencilLine } from 'lucide-react'
import {
  defaultPlans,
  followLabel,
  offsetLabel,
  offsetProblem,
  storedDraft,
  twinHeldBy,
  type MappingAccess,
  type MappingLeadRoom,
  type MappingRoom,
  type OffsetDraft,
  type OffsetKind,
  type PlanInstruction,
  type RoomDraft,
} from '@/lib/room-mapping'

interface RoomMappingRoomDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  room: MappingRoom
  /** Every room of the listing and what is staged for each, so a lead room another room holds is shown taken. */
  rooms: MappingRoom[]
  drafts: Record<number, RoomDraft>
  leadRooms: MappingLeadRoom[]
  leadName: string
  addedOptionLabel: string
  access: MappingAccess
  currencySymbol: string
  /** Copied into the dialog's own draft on mount. */
  draft: RoomDraft
  onApply: (draft: RoomDraft) => void
}

const ADDED = 'added'
const NONE = 'none'
const FOLLOW = 'follow'

// THE ROOM EDITOR OF THE ROOM MAPPING PAGE (combined inventory §3.20), in the room editor's own style
// (RoomTypeModal). Three sections, top to bottom, because each depends on the one above it:
//
//   This room is      Same room as <lead room> | Added room
//   Offset from <twin> % or an amount, % first and the default; blank is no offset
//   Rate plans        each active plan copies a plan of the twin, follows this room's lead plan by an
//                     offset (never the lead plan itself), or is not priced; a plan off in the PMS is
//                     listed and never offered a choice
//
// Like every room editor it only STAGES: Apply hands the draft back, and nothing is saved until Review &
// Save, whose review the server writes. Every choice listed is the server's (the plans each plan can soundly
// copy, the name suggestions); a person who may change only offsets sees the rest locked.
export default function RoomMappingRoomDialog({
  open,
  onOpenChange,
  room,
  rooms,
  drafts,
  leadRooms,
  leadName,
  addedOptionLabel,
  access,
  currencySymbol: sym,
  draft: initial,
  onApply,
}: RoomMappingRoomDialogProps) {
  const [draft, setDraft] = useState<RoomDraft>(() => ({ ...initial, offset: { ...initial.offset }, plans: { ...initial.plans } }))
  const [errors, setErrors] = useState<{ offset?: string; plans: Record<number, string> }>({ plans: {} })

  const offsetsOnly = access === 'offsets'
  const stored = storedDraft(room)
  const twin = draft.kind === 'same' ? leadRooms.find((lead) => lead.id === draft.sameAsRoomTypeId) ?? null : null
  const leadPlan = room.plans.find((plan) => plan.id === room.leadPlanId) ?? null
  const activePlans = room.plans.filter((plan) => plan.active)
  const inactivePlans = room.plans.filter((plan) => !plan.active)

  function chooseKind(value: string) {
    if (value === ADDED) {
      setDraft((prev) => ({ ...prev, kind: 'added', sameAsRoomTypeId: null, plans: {} }))
      return
    }
    const twinId = Number(value)
    setDraft((prev) => {
      const plans = defaultPlans(room, twinId)
      // A plan already set to follow in this dialog keeps following: it follows this room's lead plan.
      for (const [id, instruction] of Object.entries(prev.plans)) {
        if (instruction.kind === 'follow' && plans[Number(id)]) plans[Number(id)] = instruction
      }
      const offset = prev.kind === 'same' ? prev.offset : room.kind === 'same' ? stored.offset : { offsetType: 'percentage' as const, offsetValue: '' }
      return { ...prev, kind: 'same', sameAsRoomTypeId: twinId, plans, offset }
    })
  }

  function patchOffset(next: Partial<OffsetDraft>) {
    setDraft((prev) => ({ ...prev, offset: { ...prev.offset, ...next } }))
    if (errors.offset) setErrors((prev) => ({ ...prev, offset: undefined }))
  }

  function setPlan(planId: number, instruction: PlanInstruction) {
    setDraft((prev) => ({ ...prev, plans: { ...prev.plans, [planId]: instruction } }))
    if (errors.plans[planId]) setErrors((prev) => ({ ...prev, plans: { ...prev.plans, [planId]: '' } }))
  }

  function choosePlan(planId: number, value: string) {
    const current = draft.plans[planId]
    if (value === NONE) return setPlan(planId, { kind: 'none', twinPlanId: null, offsetType: 'percentage', offsetValue: '' })
    if (value === FOLLOW) {
      return setPlan(planId, {
        kind: 'follow',
        twinPlanId: null,
        offsetType: current?.kind === 'follow' ? current.offsetType : 'percentage',
        offsetValue: current?.kind === 'follow' ? current.offsetValue : '',
      })
    }
    setPlan(planId, { kind: 'copy', twinPlanId: Number(value), offsetType: 'percentage', offsetValue: '' })
  }

  function handleApply() {
    const next: { offset?: string; plans: Record<number, string> } = { plans: {} }
    if (draft.kind === 'same') {
      const problem = offsetProblem(draft.offset)
      if (problem) next.offset = problem
      for (const [id, instruction] of Object.entries(draft.plans)) {
        if (instruction.kind !== 'follow') continue
        const planProblem = offsetProblem(instruction)
        if (planProblem) next.plans[Number(id)] = planProblem
      }
    }
    if (next.offset || Object.values(next.plans).some(Boolean)) {
      setErrors(next)
      return
    }
    onApply({
      ...draft,
      offset: { ...draft.offset, offsetValue: draft.offset.offsetValue.trim() },
    })
    onOpenChange(false)
  }

  const kindValue = draft.kind === 'added' ? ADDED : draft.sameAsRoomTypeId != null ? String(draft.sameAsRoomTypeId) : ''

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-4 w-4 text-primary" aria-hidden="true" />
            {room.name}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {room.meta}. A Same room sells at the price of the same room at {leadName}; an Added room is priced on its own.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-5">
          {offsetsOnly && (
            <p className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>You can change the offsets. An owner or admin changes what this room is and which rate plans it copies.</span>
            </p>
          )}

          {/* ── This room is ─────────────────────────────────────────────────────────── */}
          <section className="space-y-2">
            <Label htmlFor={`room-kind-${room.id}`} className="text-sm font-semibold text-foreground">
              This room is
            </Label>
            <Select value={kindValue} onValueChange={chooseKind} disabled={offsetsOnly}>
              <SelectTrigger id={`room-kind-${room.id}`} className="h-9 w-full text-sm" aria-label={`What ${room.name} is`}>
                <SelectValue placeholder="Choose one" />
              </SelectTrigger>
              <SelectContent>
                {leadRooms.map((lead) => {
                  const heldBy = twinHeldBy(rooms, drafts, lead.id, room.id)
                  return (
                    <SelectItem key={lead.id} value={String(lead.id)} disabled={heldBy != null} className="text-sm">
                      {lead.optionLabel}
                      {heldBy ? ` · chosen for ${heldBy.name}` : room.suggestedTwinId === lead.id ? ' · suggested' : ''}
                    </SelectItem>
                  )
                })}
                {leadRooms.length > 0 && <SelectSeparator />}
                <SelectItem value={ADDED} className="text-sm">
                  {addedOptionLabel}
                </SelectItem>
              </SelectContent>
            </Select>
          </section>

          {twin && (
            <>
              {/* ── Offset from the twin ─────────────────────────────────────────────── */}
              <section className="space-y-2">
                <Label htmlFor={`room-offset-${room.id}`} className="text-sm font-semibold text-foreground">
                  Offset from {twin.name}
                </Label>
                <OffsetInput
                  id={`room-offset-${room.id}`}
                  draft={draft.offset}
                  sym={sym}
                  invalid={!!errors.offset}
                  onChange={patchOffset}
                />
                {errors.offset ? (
                  <p className="text-[11px] font-medium text-rose-600">{errors.offset}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {offsetLabel(draft.offset, sym)
                      ? `${room.name} sells at ${twin.name}'s price ${offsetLabel(draft.offset, sym)}.`
                      : `${room.name} sells at ${twin.name}'s price.`}{' '}
                    Use a minus for a lower price; leave it empty for none. Restrictions and rooms left to sell follow{' '}
                    {twin.name}.
                  </p>
                )}
              </section>

              {/* ── Rate plans ───────────────────────────────────────────────────────── */}
              <section className="space-y-2">
                <p className="text-sm font-semibold text-foreground">Rate plans</p>
                <div className="space-y-2">
                  {activePlans.map((plan) => {
                    const instruction = draft.plans[plan.id] ?? { kind: 'none', twinPlanId: null, offsetType: 'percentage', offsetValue: '' }
                    const options = (plan.copyOptions[String(twin.id)] ?? [])
                      .map((id) => twin.plans.find((candidate) => candidate.id === id))
                      .filter((candidate): candidate is NonNullable<typeof candidate> => candidate != null)
                    const canFollow = !plan.isLead && leadPlan != null
                    const value =
                      instruction.kind === 'copy' && instruction.twinPlanId != null
                        ? String(instruction.twinPlanId)
                        : instruction.kind === 'follow'
                          ? FOLLOW
                          : NONE
                    // A person who may change only offsets edits the offset of a plan that follows now.
                    const followOffsetLocked = offsetsOnly && stored.plans[plan.id]?.kind !== 'follow'
                    const planError = errors.plans[plan.id]
                    return (
                      <div
                        key={plan.id}
                        className={cn(
                          'space-y-2 rounded-lg border bg-white p-3',
                          instruction.kind === 'none' ? 'border-amber-300/70 ring-1 ring-amber-100' : 'border-border/60',
                        )}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="flex min-w-0 items-center gap-1.5">
                            {plan.isLead && <Crown className="h-3 w-3 shrink-0 text-primary" aria-label="Lead rate plan" />}
                            <span className="truncate text-sm font-medium text-foreground">{plan.name}</span>
                          </span>
                          {instruction.kind === 'none' && (
                            <Badge variant="secondary" className="shrink-0 border-transparent bg-amber-100 text-[11px] font-medium text-amber-700">
                              Not priced
                            </Badge>
                          )}
                        </div>
                        <Select value={value} onValueChange={(next) => choosePlan(plan.id, next)} disabled={offsetsOnly}>
                          <SelectTrigger className="h-9 w-full text-sm" aria-label={`What ${plan.name} does`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {options.map((option) => (
                              <SelectItem key={option.id} value={String(option.id)} className="text-sm">
                                Copies {option.name}
                              </SelectItem>
                            ))}
                            {/* A pair saved before its plans changed in the PMS: shown as it is, never offered. */}
                            {instruction.kind === 'copy' && instruction.twinPlanId != null && !options.some((option) => option.id === instruction.twinPlanId) && (
                              <SelectItem value={String(instruction.twinPlanId)} disabled className="text-sm">
                                Copies a plan of {twin.name} it can&apos;t copy
                              </SelectItem>
                            )}
                            {canFollow && leadPlan && (
                              <SelectItem value={FOLLOW} className="text-sm">
                                Follows {leadPlan.name}
                              </SelectItem>
                            )}
                            <SelectSeparator />
                            <SelectItem value={NONE} className="text-sm">
                              Not priced
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        {instruction.kind === 'follow' && leadPlan && (
                          <div className="space-y-1">
                            <OffsetInput
                              id={`plan-offset-${plan.id}`}
                              draft={instruction}
                              sym={sym}
                              invalid={!!planError}
                              disabled={followOffsetLocked}
                              placeholder="0"
                              ariaLabel={`${plan.name} offset from ${leadPlan.name}`}
                              onChange={(next) => setPlan(plan.id, { ...instruction, ...next })}
                            />
                            {planError ? (
                              <p className="text-[11px] font-medium text-rose-600">{planError}</p>
                            ) : (
                              <p className="text-[11px] text-muted-foreground">
                                {plan.name} = {leadPlan.name} {followLabel(instruction, sym)}, after this room&apos;s own offset.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                  {inactivePlans.map((plan) => (
                    <div
                      key={plan.id}
                      className="flex items-center justify-between gap-2 rounded-lg border border-dashed border-border/60 px-3 py-2.5"
                    >
                      <span className="truncate text-sm text-muted-foreground">{plan.name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">Off in your PMS</span>
                    </div>
                  ))}
                  {room.plans.length === 0 && (
                    <p className="text-xs text-muted-foreground">This room has no rate plans in your PMS.</p>
                  )}
                </div>
              </section>
            </>
          )}

          {draft.kind === 'added' && (
            <p className="text-xs leading-relaxed text-muted-foreground">
              An Added room is a room {leadName} does not have. Its base rate, floor, ceiling and rate plans are set in Rooms
              Setup.
            </p>
          )}
        </DialogBody>

        <DialogFooter className="flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <PencilLine className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
            Staged only, not saved yet. Cancel discards these edits.
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" className="rounded-lg" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" className="rounded-lg" onClick={handleApply} disabled={draft.kind === 'same' && !twin}>
              Apply
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── An offset: [% | £] then the value, % first and the default ────────────────────────────────────
export function OffsetInput({
  id,
  draft,
  sym,
  invalid,
  disabled = false,
  placeholder = 'No offset',
  ariaLabel,
  onChange,
}: {
  id: string
  draft: OffsetDraft
  sym: string
  invalid: boolean
  disabled?: boolean
  placeholder?: string
  ariaLabel?: string
  onChange: (next: Partial<OffsetDraft>) => void
}) {
  const kinds: { kind: OffsetKind; text: string }[] = [
    { kind: 'percentage', text: '%' },
    { kind: 'absolute', text: sym.trim() },
  ]
  return (
    <div className="flex items-center gap-2">
      <div className="flex overflow-hidden rounded-lg border border-border/60" role="group" aria-label="Offset kind">
        {kinds.map(({ kind, text }) => (
          <button
            key={kind}
            type="button"
            disabled={disabled}
            aria-pressed={draft.offsetType === kind}
            className={cn(
              'cursor-pointer px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-50',
              draft.offsetType === kind ? 'bg-primary text-white' : 'bg-white text-muted-foreground hover:bg-muted',
            )}
            onClick={() => onChange({ offsetType: kind })}
          >
            {text}
          </button>
        ))}
      </div>
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        step={draft.offsetType === 'percentage' ? '0.1' : '0.01'}
        disabled={disabled}
        aria-label={ariaLabel}
        className={cn('flex-1 rounded-lg tabular-nums', invalid && 'border-rose-300 focus:border-rose-400 focus:ring-rose-400/20')}
        placeholder={placeholder}
        value={draft.offsetValue}
        onChange={(e) => onChange({ offsetValue: e.target.value })}
      />
    </div>
  )
}
